<template>
  <NavBar pageName="Good Categories"></NavBar>
  <v-container style="margin-top: 185px;">
    <v-btn variant="text" size="small" @click="$router.push('/settings/goods')" class="mb-2">
      <v-icon size="small" class="mr-1">mdi-arrow-left</v-icon>
      Goods Settings
    </v-btn>
    <v-card>
      <v-card-title class="d-flex justify-space-between align-center">
        <span>Good Categories</span>
        <v-btn color="success" size="small" @click="openAddDialog">
          <v-icon class="mr-1">mdi-plus</v-icon>
          Add
        </v-btn>
      </v-card-title>

      <v-card-text>
        <v-data-table
          :headers="headers"
          :items="categories"
          :loading="loading"
          :items-per-page="20"
          class="elevation-1"
        >
          <template v-slot:[`item.created_at`]="{ item }">
            {{ formatDate(item.created_at) }}
          </template>

          <template v-slot:[`item.actions`]="{ item }">
            <v-btn
              icon
              size="small"
              variant="text"
              color="primary"
              @click="editCategory(item)"
            >
              <v-icon>mdi-pencil</v-icon>
            </v-btn>
            <v-btn
              icon
              size="small"
              variant="text"
              color="error"
              @click="confirmDelete(item)"
            >
              <v-icon>mdi-delete</v-icon>
            </v-btn>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>

    <!-- Add/Edit Dialog -->
    <v-dialog v-model="showDialog" max-width="500px" persistent>
      <v-card>
        <v-card-title>
          {{ editingId ? 'Edit' : 'Add' }} Good Category
        </v-card-title>

        <v-card-text>
          <v-text-field
            v-model="formData.name"
            label="Name"
            variant="outlined"
            density="compact"
            :rules="[v => !!v || 'Name is required']"
          ></v-text-field>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="closeDialog">Cancel</v-btn>
          <v-btn
            color="primary"
            variant="flat"
            @click="saveCategory"
            :loading="saving"
            :disabled="!formData.name"
          >
            Save
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete Confirmation Dialog -->
    <v-dialog v-model="showDeleteDialog" max-width="400px">
      <v-card>
        <v-card-title>Confirm Delete</v-card-title>
        <v-card-text>
          Are you sure you want to delete this category? This action cannot be undone.
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="showDeleteDialog = false">Cancel</v-btn>
          <v-btn color="error" variant="flat" @click="deleteCategory" :loading="deleting">
            Delete
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Snackbar -->
    <v-snackbar v-model="showSnackbar" :color="snackbarColor" :timeout="3000">
      {{ snackbarMessage }}
    </v-snackbar>
  </v-container>
</template>

<script>
import { defineComponent } from 'vue';
import NavBar from "@/components/NavBar.vue";
import store from "@/store";
import { log } from "@/composables/useLog";
import { getGoodCategories, saveGoodCategory, deleteGoodCategory as deleteGoodCategoryAPI } from "@/composables/useGoodsInventoryAPI";

export default defineComponent({
  name: 'GoodCategoriesList',
  components: {
    NavBar
  },
  data() {
    return {
      loading: false,
      saving: false,
      deleting: false,
      categories: [],
      showDialog: false,
      showDeleteDialog: false,
      editingId: null,
      deleteId: null,
      formData: {
        name: ''
      },
      showSnackbar: false,
      snackbarMessage: '',
      snackbarColor: 'success'
    }
  },
  computed: {
    token() {
      return store.getters.getToken;
    },
    headers() {
      return [
        { title: 'ID', key: 'id', width: '80px' },
        { title: 'Name', key: 'name' },
        { title: 'Created At', key: 'created_at', width: '180px' },
        { title: 'Actions', key: 'actions', sortable: false, width: '120px' }
      ];
    }
  },
  methods: {
    async loadCategories() {
      this.loading = true;
      try {
        const response = await getGoodCategories(this.token);
        if (!response.error) {
          this.categories = response.good_categories || [];
        } else {
          this.showMessage(response.message || 'Error loading categories', 'error');
        }
      } catch (err) {
        log("Error loading categories:", err);
        this.showMessage('Error loading categories', 'error');
      } finally {
        this.loading = false;
      }
    },

    openAddDialog() {
      this.editingId = null;
      this.formData = { name: '' };
      this.showDialog = true;
    },

    editCategory(item) {
      this.editingId = item.id;
      this.formData = { name: item.name };
      this.showDialog = true;
    },

    closeDialog() {
      this.showDialog = false;
      this.editingId = null;
      this.formData = { name: '' };
    },

    async saveCategory() {
      this.saving = true;
      try {
        const data = {
          id: this.editingId,
          name: this.formData.name
        };

        const response = await saveGoodCategory(this.token, data);
        if (!response.error) {
          this.showMessage(response.message || 'Category saved', 'success');
          this.closeDialog();
          await this.loadCategories();
        } else {
          this.showMessage(response.message || 'Error saving category', 'error');
        }
      } catch (err) {
        log("Error saving category:", err);
        this.showMessage('Error saving category', 'error');
      } finally {
        this.saving = false;
      }
    },

    confirmDelete(item) {
      this.deleteId = item.id;
      this.showDeleteDialog = true;
    },

    async deleteCategory() {
      this.deleting = true;
      try {
        const response = await deleteGoodCategoryAPI(this.token, this.deleteId);
        if (!response.error) {
          this.showMessage(response.message || 'Category deleted', 'success');
          this.showDeleteDialog = false;
          this.deleteId = null;
          await this.loadCategories();
        } else {
          this.showMessage(response.message || 'Error deleting category', 'error');
        }
      } catch (err) {
        log("Error deleting category:", err);
        this.showMessage('Error deleting category', 'error');
      } finally {
        this.deleting = false;
      }
    },

    formatDate(dateString) {
      if (!dateString) return '-';
      const date = new Date(dateString);
      return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    },

    showMessage(message, color) {
      this.snackbarMessage = message;
      this.snackbarColor = color;
      this.showSnackbar = true;
    }
  },
  mounted() {
    this.loadCategories();
  }
});
</script>
